import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import type {
  CommercialAddModulesResult,
  CommercialCheckoutResult,
  CommercialPaymentProvider,
  ProductCard,
  SolutionCard,
} from "@itaas/pep-contracts";
import { getProducts, getSolutions, getProvisionStatus, submitLead, startCommercialTrial, addCommercialModules, createCommercialCheckout } from "../api/pep-api";
import type { CommercialSignupMode, CommercialTrialResult, CommercialBillingCycle } from "@itaas/pep-contracts";
import { SiteLayout } from "../components/layout/SiteLayout";
import { SeoHead } from "../components/seo/SeoHead";
import { SUITE_URL } from "../config";
import {
  buildBiometricConfirmationMessage,
  buildBiometricLeadNote,
  DEFAULT_BIOMETRIC_TERMINALS,
  filterTrialEligibleProducts,
  isEducationTrial,
  needsBiometricHardware,
} from "../utils/biometric-trial";
import { buildSuiteLoginUrl, buildTrialDestinationUrl } from "../utils/trial-redirect";
import { billingCycleLabel } from "../utils/billing-cycle";
import { formatTrialDaysLabel, resolveTrialDays } from "../utils/trial-period";
import { getAffiliateCodeForLead, setManualAffiliateCode, getManualAffiliateCode, getStoredAffiliateRef } from "../utils/affiliate-attribution";
import { TrialActivatedPanel } from "./trial/TrialActivatedPanel";
import { TrialModulesAddedPanel } from "./trial/TrialModulesAddedPanel";
import { TrialModeStep } from "./trial/TrialModeStep";
import { TrialEduEditionStep } from "./trial/TrialEduEditionStep";
import { TrialHospitolEditionStep } from "./trial/TrialHospitolEditionStep";
import type { EduEditionId } from "@itaas/edu-editions";
import type { HospitolEditionId } from "@itaas/hospitol-editions";
import { TrialModulePicker } from "./trial/TrialModulePicker";
import { GeoLocationFields } from "./trial/GeoLocationFields";
import { PhoneFieldWithDialCode, syncDialCodeFromCountry } from "./trial/PhoneFieldWithDialCode";
import { TrialTenantSlugStep } from "./trial/TrialTenantSlugStep";
import { combineTrialPhone } from "./trial/trial-phone.utils";
import {
  consumeTrialHandoffFromLocation,
  loadTrialSession,
  type TrialIamSession,
} from "../utils/trial-session";
import {
  isTechnicalTrialEntry,
  marketingTrialSearchToConfigureHref,
} from "../utils/commercial-cta";

function parseProductList(raw: string | null): string[] {
  if (!raw?.trim()) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

type TrialStep = 1 | 2 | 3 | 4 | 5;
type AccountKind = "new" | "existing";

function StepIndicator({ step }: { step: TrialStep }) {
  const items = [
    { n: 1, label: "Solutions" },
    { n: 2, label: "Édition" },
    { n: 3, label: "Organisation" },
    { n: 4, label: "Espace" },
    { n: 5, label: "Confirmation" },
  ] as const;
  return (
    <ol className="pep-trial-steps mb-4">
      {items.map(({ n, label }) => (
        <li key={n} className={step >= n ? "active" : ""} aria-current={step === n ? "step" : undefined}>
          <span className="pep-trial-steps__n">{n}</span>
          <span className="pep-trial-steps__label">{label}</span>
        </li>
      ))}
    </ol>
  );
}

const PAYMENT_LABELS: Record<CommercialPaymentProvider, string> = {
  stripe: "Carte bancaire (Stripe)",
  paypal: "PayPal",
  mpesa: "M-Pesa",
};

export function TrialPage() {
  const [params] = useSearchParams();
  /** V1.0.1 — marketing /trial → Configure; technical handoff/session kept. */
  const technicalTrial = isTechnicalTrialEntry(params, Boolean(loadTrialSession()));
  if (!technicalTrial) {
    return <Navigate to={marketingTrialSearchToConfigureHref(params)} replace />;
  }
  return <TrialPageTechnical />;
}

function TrialPageTechnical() {
  const [params] = useSearchParams();
  const preselectProduct = params.get("product") ?? "";
  const preselectProductsParam = params.get("products");
  const industrySlug = params.get("industry");
  const preselectProducts = useMemo(
    () => parseProductList(preselectProductsParam),
    [preselectProductsParam],
  );
  const preselectFromUrl = useMemo(() => {
    if (preselectProducts.length) return preselectProducts;
    if (preselectProduct) return [preselectProduct];
    return [];
  }, [preselectProduct, preselectProducts]);

  const [step, setStep] = useState<TrialStep>(1);
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [solutions, setSolutions] = useState<SolutionCard[]>([]);
  const [selected, setSelected] = useState<string[]>(() => preselectFromUrl);
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("+243");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [countryCode, setCountryCode] = useState("CD");
  const [countryName, setCountryName] = useState("Congo (Dem. Rep.)");
  const [stateCode, setStateCode] = useState("");
  const [province, setProvince] = useState("");
  const [city, setCity] = useState("");
  const [taxId, setTaxId] = useState("");
  const [rccm, setRccm] = useState("");
  const [biometricCount, setBiometricCount] = useState(DEFAULT_BIOMETRIC_TERMINALS);
  const [classroomCount, setClassroomCount] = useState<number | "">("");
  const [trialResult, setTrialResult] = useState<CommercialTrialResult | null>(null);
  const [addModulesResult, setAddModulesResult] = useState<CommercialAddModulesResult | null>(null);
  const [mpesaPending, setMpesaPending] = useState<CommercialCheckoutResult | null>(null);
  const [signupMode, setSignupMode] = useState<CommercialSignupMode>("trial");
  const [billingCycle, setBillingCycle] = useState<CommercialBillingCycle>("monthly");
  const [eduEdition, setEduEdition] = useState<EduEditionId>("standard");
  const [hospitolEdition, setHospitolEdition] = useState<HospitolEditionId>("clinic");
  const [paymentProvider, setPaymentProvider] = useState<CommercialPaymentProvider | null>(null);
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [accountKind, setAccountKind] = useState<AccountKind | null>(null);
  const [trialSession, setTrialSession] = useState<TrialIamSession | null>(null);
  const [existingTenantSlug, setExistingTenantSlug] = useState("");
  const [existingPassword, setExistingPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leadOnly, setLeadOnly] = useState(false);
  const [provisionBlocked, setProvisionBlocked] = useState<string | null>(null);
  const [affiliateCodeInput, setAffiliateCodeInput] = useState(
    () => getManualAffiliateCode() ?? getStoredAffiliateRef()?.code ?? "",
  );

  useEffect(() => {
    const session = consumeTrialHandoffFromLocation();
    if (session) {
      setTrialSession(session);
      setAccountKind("existing");
      setExistingTenantSlug(session.tenantSlug);
      setEmail(session.email);
      setCompany(session.tenantName);
    }
    const stepParam = params.get("step");
    if (stepParam === "3" || params.get("existing") === "1") {
      setStep(3);
      if (!session && params.get("existing") === "1") {
        setAccountKind("existing");
      }
    }
  }, [params]);

  useEffect(() => {
    Promise.all([getProducts(), getSolutions()])
      .then(([prods, sols]) => {
        setProducts(prods);
        setSolutions(sols);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    void getProvisionStatus()
      .then((status) => {
        if (!status.iamConfigured) {
          setProvisionBlocked(
            "Provision automatique désactivée (IAM_PROVISION_TOKEN manquant côté serveur PEP).",
          );
          return;
        }
        if (!status.iamReachable) {
          setProvisionBlocked(
            `Service IAM injoignable (${status.iamApiUrl}). Démarrez l'API IAM (port 3030) ou vérifiez IAM_API_URL.`,
          );
        }
      })
      .catch(() => undefined);
  }, []);

  // Resync uniquement si les paramètres URL de présélection changent (pas à chaque render).
  useEffect(() => {
    setSelected(preselectFromUrl);
  }, [preselectProduct, preselectProductsParam]);

  const allTrialProducts = filterTrialEligibleProducts(products);
  const trialProducts =
    industrySlug && preselectProducts.length
      ? allTrialProducts.filter(
          (p) => preselectProducts.includes(p.slug) || p.industries?.includes(industrySlug),
        )
      : allTrialProducts;

  const showBiometricFields = needsBiometricHardware(selected);
  const educationContext = isEducationTrial(industrySlug, selected);

  function resolveTrialEdition(): string {
    if (selected.includes("edu")) return eduEdition;
    if (selected.includes("hospitol")) return hospitolEdition;
    return "professional";
  }

  function toggle(slug: string) {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  function setSelectedModules(slugs: string[] | ((prev: string[]) => string[])) {
    setSelected(slugs);
  }

  function buildLeadMessage(biometricNote?: string) {
    const lines = [
      biometricNote,
      `Cycle de facturation : ${billingCycleLabel(billingCycle)}`,
      countryName ? `Pays : ${countryName}` : undefined,
      province ? `Province : ${province}` : undefined,
      city ? `Ville : ${city}` : undefined,
      taxId ? `N° impôt : ${taxId}` : undefined,
      rccm ? `RCCM : ${rccm}` : undefined,
    ].filter(Boolean);
    return lines.length ? lines.join("\n") : undefined;
  }

  function buildPayload() {
    const affiliateCode = affiliateCodeInput.trim().toUpperCase() || getAffiliateCodeForLead();
    const phoneE164 = combineTrialPhone(phoneDialCode, phoneLocal);
    return {
      company,
      name: name.trim() || company,
      email,
      phone: phoneE164,
      tenantSlug: tenantSlug.trim().toLowerCase() || undefined,
      modules: selected,
      edition: resolveTrialEdition(),
      eduEdition: selected.includes("edu") ? eduEdition : undefined,
      hospitolEdition: selected.includes("hospitol") ? hospitolEdition : undefined,
      billingCycle,
      mode: signupMode as "trial" | "pay",
      paymentProvider: paymentProvider ?? undefined,
      mpesaPhone: paymentProvider === "mpesa" ? mpesaPhone.trim() : undefined,
      affiliateCode,
      industrySlug: industrySlug ?? undefined,
      locale: "fr" as const,
      country: countryName || countryCode,
      province,
      city,
      taxId: taxId.trim(),
      rccm: rccm.trim() || undefined,
    };
  }

  function goToStep2() {
    setError(null);
    if (selected.length === 0) {
      setError("Sélectionnez au moins un module.");
      return;
    }
    setStep(2);
  }

  const licensedModules = trialSession?.modules ?? [];
  const modulesToActivate = selected.filter((m) => !licensedModules.includes(m));

  function goToStep3() {
    setError(null);
    if (!signupMode) {
      setError("Choisissez une édition pour continuer.");
      return;
    }
    if (signupMode === "pay" && !paymentProvider) {
      setError("Choisissez un mode de paiement.");
      return;
    }
    setStep(3);
  }

  function onSubmitStep3(e: FormEvent) {
    e.preventDefault();
    if (!validateStep3()) return;

    const isExistingAccount = accountKind === "existing" || Boolean(trialSession);
    if (isExistingAccount) {
      runActivation();
      return;
    }
    setError(null);
    setStep(4);
  }

  function validateStep3(): boolean {
    if (!signupMode) {
      setError("Choisissez une formule pour continuer.");
      return false;
    }
    if (signupMode === "pay" && !paymentProvider) {
      setError("Choisissez un mode de paiement.");
      return false;
    }

    const isExistingAccount = accountKind === "existing" || Boolean(trialSession);

    if (isExistingAccount && signupMode === "pay") {
      setError(
        "L'ajout de modules payants à un compte existant se fait via notre équipe commerciale — choisissez l'essai gratuit ou contactez-nous.",
      );
      return false;
    }

    if (isExistingAccount) {
      if (!accountKind) setAccountKind("existing");
      if (modulesToActivate.length === 0) {
        setError("Les modules sélectionnés sont déjà actifs sur votre organisation.");
        return false;
      }
      if (!trialSession) {
        if (!existingTenantSlug.trim() || !email.trim() || !existingPassword.trim()) {
          setError("Identifiez-vous avec votre organisation, email et mot de passe.");
          return false;
        }
      }
      return true;
    }

    if (!accountKind) {
      setError("Indiquez si vous créez une nouvelle organisation ou si vous avez déjà un compte.");
      return false;
    }

    const phoneE164 = combineTrialPhone(phoneDialCode, phoneLocal);
    if (phoneLocal.trim().length < 8 || phoneE164.length < 10) {
      setError("Indiquez un numéro de téléphone valide (sans l'indicatif dans le champ numéro).");
      return false;
    }

    if (signupMode === "pay" && paymentProvider === "mpesa" && mpesaPhone.trim().length < 9) {
      setError("Indiquez un numéro M-Pesa valide.");
      return false;
    }

    setError(null);
    return true;
  }

  function runActivation() {
    setSubmitting(true);
    setError(null);
    setLeadOnly(false);
    setMpesaPending(null);
    setAddModulesResult(null);
    setTrialResult(null);

    const isExistingAccount = accountKind === "existing" || Boolean(trialSession);

    const biometricNote = showBiometricFields
      ? buildBiometricLeadNote({
          industrySlug,
          terminalCount: biometricCount,
          classroomCount: classroomCount === "" ? undefined : classroomCount,
        })
      : undefined;

    const payload = buildPayload();
    const phoneE164 = payload.phone;

    const leadPromise = submitLead({
      company: company || trialSession?.tenantName || existingTenantSlug,
      name: name.trim() || company || trialSession?.tenantName || "Client existant",
      email,
      phone: phoneE164 || "—",
      country: countryName || countryCode,
      province,
      city,
      taxId: taxId.trim(),
      rccm: rccm.trim() || undefined,
      type: signupMode === "pay" ? "devis" : "trial",
      products: selected,
      source: trialSession
        ? "pep-trial-existing-session"
        : isExistingAccount
          ? "pep-trial-existing-login"
          : industrySlug
            ? `pep-trial-${industrySlug}`
            : "pep-trial",
      locale: "fr",
      industrySlug: industrySlug ?? undefined,
      biometricTerminalCount: showBiometricFields ? biometricCount : undefined,
      classroomCount: classroomCount === "" ? undefined : classroomCount,
      message: buildLeadMessage(biometricNote),
      billingCycle,
      affiliateCode: affiliateCodeInput.trim().toUpperCase() || getAffiliateCodeForLead(),
    }).catch(() => undefined);

    if (isExistingAccount && signupMode === "trial") {
      void Promise.all([
        leadPromise,
        addCommercialModules({
          modules: modulesToActivate,
          mode: "trial",
          accessToken: trialSession?.accessToken,
          tenantSlug: trialSession?.tenantSlug ?? existingTenantSlug.trim(),
          email: email.trim(),
          password: trialSession ? undefined : existingPassword,
          company: company || trialSession?.tenantName,
          name: name.trim() || undefined,
          phone: phoneE164 || undefined,
          affiliateCode: affiliateCodeInput.trim().toUpperCase() || getAffiliateCodeForLead(),
          industrySlug: industrySlug ?? undefined,
          locale: "fr",
        }),
      ])
        .then(([, result]) => {
          setAddModulesResult(result);
          setStep(5);
        })
        .catch((err) => {
          console.error(err);
          const detail = err instanceof Error ? err.message : null;
          setError(
            detail && !detail.startsWith("API ")
              ? detail
              : "Activation indisponible. Votre demande est enregistrée — notre équipe vous contactera.",
          );
          void leadPromise.then(() => {
            setLeadOnly(true);
            setStep(5);
          });
        })
        .finally(() => setSubmitting(false));
      return;
    }

    if (signupMode === "pay") {
      void createCommercialCheckout(payload)
        .then(async (checkout) => {
          await leadPromise;
          if (checkout.paymentProvider === "mpesa") {
            setMpesaPending(checkout);
            setStep(5);
            setSubmitting(false);
            return;
          }
          if (checkout.checkoutUrl) {
          window.location.href = checkout.checkoutUrl;
            return;
          }
          setError("Paiement indisponible — URL manquante.");
          setSubmitting(false);
        })
        .catch((err) => {
          console.error(err);
          setError(
            "Paiement indisponible pour le moment. Essayez l'essai gratuit ou contactez-nous.",
          );
          setSubmitting(false);
        });
      return;
    }

    void Promise.all([leadPromise, startCommercialTrial(payload)])
      .then(([, trial]) => {
        setTrialResult(trial);
        setStep(5);
      })
      .catch((err) => {
        console.error(err);
        const detail = err instanceof Error ? err.message : null;
        setError(
          detail && !detail.startsWith("API ")
            ? detail
            : "Provision automatique indisponible. Votre demande est enregistrée — notre équipe vous contactera.",
        );
        void leadPromise.then(() => {
          setLeadOnly(true);
          setStep(5);
        });
      })
      .finally(() => setSubmitting(false));
  }

  function continueToApp() {
    if (addModulesResult) {
      window.location.href = addModulesResult.loginUrl;
      return;
    }
    if (trialResult) {
      window.location.href = buildSuiteLoginUrl(SUITE_URL, {
        tenantSlug: trialResult.tenantSlug,
        adminEmail: trialResult.adminEmail,
        module: trialResult.modules[0],
      });
      return;
    }
    window.location.href = buildTrialDestinationUrl(
      selected,
      { email, company, ref: getAffiliateCodeForLead() },
      SUITE_URL,
    );
  }

  const biometricMessage =
    trialResult && showBiometricFields
    ? buildBiometricConfirmationMessage({
        industrySlug,
        productSlugs: selected,
        terminalCount: biometricCount,
        classroomCount: classroomCount === "" ? undefined : classroomCount,
      })
    : null;

  const paymentLabel =
    signupMode === "pay" && paymentProvider ? PAYMENT_LABELS[paymentProvider] : null;

  const selectedTrialDays = useMemo(
    () => resolveTrialDays(products, selected),
    [products, selected],
  );
  const trialTitle = formatTrialDaysLabel(selectedTrialDays);
  const eduProduct = useMemo(
    () => products.find((p) => p.slug === "edu") ?? null,
    [products],
  );
  const hospitolProduct = useMemo(
    () => products.find((p) => p.slug === "hospitol") ?? null,
    [products],
  );

  return (
    <SiteLayout>
      <SeoHead
        title={trialTitle}
        description={`Démarrez votre essai ITaaS${selectedTrialDays ? ` (${selectedTrialDays} jours)` : ""} — sélectionnez vos modules et accédez à la Suite.`}
        canonicalPath="/trial"
      />
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-9 col-xl-8">
            <h1 className="display-6 fw-bold mb-2">Démarrer avec ITaaS</h1>
            <p className="text-secondary mb-4">
              Parcours guidé en 5 étapes.{" "}
              <Link
                to={`/configure?return=trial${preselectProduct ? `&product=${preselectProduct}` : ""}${industrySlug ? `&industry=${industrySlug}` : ""}`}
              >
                Package sur mesure
              </Link>
            </p>

            <StepIndicator step={step} />

            {provisionBlocked && step < 5 && (
              <div className="alert alert-warning mb-4" role="status">
                {provisionBlocked}
              </div>
            )}

            {step === 5 && addModulesResult && (
              <TrialModulesAddedPanel result={addModulesResult} onContinue={continueToApp} />
            )}

            {step === 5 && trialResult && (
              <TrialActivatedPanel
                trialResult={trialResult}
                biometricMessage={biometricMessage}
                onContinue={continueToApp}
              />
            )}

            {step === 5 && mpesaPending && (
              <div className="card border-0 shadow-sm p-4 p-md-5">
                <div className="text-center mb-4">
                  <div className="display-6 text-primary mb-2">📱</div>
                  <h2 className="h4 fw-bold">Paiement M-Pesa en cours</h2>
                  <p className="text-secondary mb-0">
                    Montant : <strong>{mpesaPending.amountFormatted}</strong>
                  </p>
                </div>
                {mpesaPending.mpesaReference && (
                  <p className="text-center font-monospace mb-3">{mpesaPending.mpesaReference}</p>
                )}
                <pre className="pep-trial-mpesa-instructions mb-4">{mpesaPending.mpesaInstructions}</pre>
                <p className="small text-secondary text-center mb-0">
                  Un récapitulatif sera envoyé à <strong>{email}</strong> après validation du paiement.
                    </p>
                  </div>
                )}

            {step === 5 && leadOnly && !trialResult && !addModulesResult && !mpesaPending && (
              <div className="card border-0 shadow-sm p-4 p-md-5 text-center">
                <div className="display-6 text-warning mb-2">!</div>
                <h2 className="h4 fw-bold">Demande enregistrée</h2>
                <p className="text-secondary">
                  {error ??
                    "Provision automatique indisponible — notre équipe vous contactera sous peu."}
                </p>
                <button type="button" className="btn btn-outline-primary mt-3" onClick={continueToApp}>
                  Retour au catalogue
                  </button>
              </div>
            )}

            {step === 1 && (
              <div className="card border-0 shadow-sm p-4">
                <h2 className="h6 fw-bold mb-1">Solutions à essayer</h2>
                <p className="text-secondary small mb-3">
                  Survolez une carte pour un résumé rapide, ou cliquez sur{" "}
                  <span className="pep-catalog-info-inline" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.75" />
                      <path d="M12 11v5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                      <circle cx="12" cy="8" r="1" fill="currentColor" />
                    </svg>
                  </span>{" "}
                  pour la fiche complète.
                </p>

                <TrialModulePicker
                  products={trialProducts}
                  solutions={solutions}
                  selected={selected}
                  onToggle={toggle}
                  onSetSelected={setSelectedModules}
                />

                {showBiometricFields && (
                  <div className="border rounded-3 p-3 mb-4 mt-4 bg-body-tertiary">
                    <h2 className="h6 fw-bold mb-3">Lecteur(s) biométrique</h2>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Quantité de lecteurs *</label>
                        <input
                          type="number"
                          className="form-control"
                          min={1}
                          value={biometricCount}
                          onChange={(e) =>
                            setBiometricCount(Math.max(1, parseInt(e.target.value, 10) || 1))
                          }
                          required
                        />
                      </div>
                      {educationContext && (
                        <div className="col-md-6">
                          <label className="form-label">Nombre de salles de classe</label>
                          <input
                            type="number"
                            className="form-control"
                            min={1}
                            placeholder="Optionnel"
                            value={classroomCount}
                            onChange={(e) => {
                              const v = e.target.value;
                              setClassroomCount(v === "" ? "" : Math.max(1, parseInt(v, 10) || 1));
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {error && <div className="alert alert-danger">{error}</div>}
                <button
                  type="button"
                  className="btn btn-primary btn-lg w-100 mt-3"
                  disabled={selected.length === 0}
                  onClick={goToStep2}
                >
                  Continuer — choisir l'édition →
                </button>
              </div>
            )}

            {step === 2 && (
              <>
                <div className="card border-0 shadow-sm p-4 mb-3">
                  <label className="form-label fw-semibold mb-1">Code parrainage (optionnel)</label>
                  <input
                    className="form-control font-monospace"
                    value={affiliateCodeInput}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setAffiliateCodeInput(v);
                      setManualAffiliateCode(v || null);
                    }}
                    placeholder="EDU-ABC123"
                  />
                  <p className="form-text mb-0">
                    Saisissez le code d&apos;un ambassadeur ITaaS ou laissez vide si vous êtes
                    arrivé via son lien (?ref=).
                  </p>
                </div>
                {selected.includes("edu") ? (
                  <TrialEduEditionStep
                    value={eduEdition}
                    onChange={setEduEdition}
                    promotion={eduProduct?.promotion}
                  />
                ) : null}
                {selected.includes("hospitol") ? (
                  <TrialHospitolEditionStep
                    value={hospitolEdition}
                    onChange={setHospitolEdition}
                    promotion={hospitolProduct?.promotion}
                  />
                ) : null}
                <TrialModeStep
                value={signupMode}
                billingCycle={billingCycle}
                trialDays={selectedTrialDays}
                paymentProvider={paymentProvider}
                onChange={(mode) => {
                  setSignupMode(mode);
                  if (mode === "trial") setPaymentProvider(null);
                }}
                onBillingCycleChange={setBillingCycle}
                onPaymentProviderChange={setPaymentProvider}
                onBack={() => setStep(1)}
                onContinue={goToStep3}
                error={error}
              />
              </>
            )}

            {step === 3 && (
              <form onSubmit={onSubmitStep3} className="card border-0 shadow-sm p-4">
                {!trialSession && (
                  <div className="mb-4">
                    <h2 className="h6 fw-bold mb-2">Votre situation</h2>
                    <div className="d-flex flex-column flex-sm-row gap-2">
                      <button
                        type="button"
                        className={`btn ${accountKind === "new" ? "btn-primary" : "btn-outline-primary"} flex-fill`}
                        onClick={() => {
                          setAccountKind("new");
                          setError(null);
                        }}
                      >
                        Nouvelle organisation
                      </button>
                      <button
                        type="button"
                        className={`btn ${accountKind === "existing" ? "btn-primary" : "btn-outline-primary"} flex-fill`}
                        onClick={() => {
                          setAccountKind("existing");
                          setError(null);
                        }}
                      >
                        J&apos;ai déjà un compte ITaaS
                      </button>
                    </div>
                    <p className="form-text mb-0 mt-2">
                      Déjà connecté depuis la Suite ? Votre session sera reconnue automatiquement.
                    </p>
                  </div>
                )}

                {trialSession && (
                  <div className="alert alert-info mb-4" role="status">
                    <strong>Session active</strong> — {trialSession.email}
                    <br />
                    Organisation : <code>{trialSession.tenantSlug}</code>
                    {licensedModules.length > 0 && (
                      <>
                        <br />
                        Modules actuels :{" "}
                        {licensedModules
                          .filter((m) => m !== "iam")
                          .map((m) => m.toUpperCase())
                          .join(", ") || "—"}
                      </>
                    )}
                  </div>
                )}

                {(accountKind === "existing" || trialSession) && (
                  <>
                    {!trialSession && (
                      <>
                        <h2 className="h6 fw-bold mb-3">Connexion administrateur</h2>
                        <div className="row g-3 mb-4">
                          <div className="col-12">
                            <label className="form-label">Identifiant organisation *</label>
                            <input
                              className="form-control font-monospace"
                              value={existingTenantSlug}
                              onChange={(e) => setExistingTenantSlug(e.target.value)}
                              placeholder="ex. demo-edu"
                              required
                              autoComplete="organization"
                            />
                            <div className="form-text">
                              Même identifiant que sur la page de connexion Suite (champ « organisation »).
                            </div>
                          </div>
                          <div className="col-12">
                            <label className="form-label">Email *</label>
                            <input
                              type="email"
                              className="form-control"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              autoComplete="username"
                            />
                          </div>
                          <div className="col-12">
                            <label className="form-label">Mot de passe *</label>
                            <input
                              type="password"
                              className="form-control"
                              value={existingPassword}
                              onChange={(e) => setExistingPassword(e.target.value)}
                              required
                              autoComplete="current-password"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {modulesToActivate.length === 0 ? (
                      <div className="alert alert-warning mb-3">
                        Tous les modules sélectionnés sont déjà actifs sur votre organisation.
                      </div>
                    ) : (
                      <p className="small text-secondary mb-3">
                        Modules à activer :{" "}
                        <strong>{modulesToActivate.map((s) => s.toUpperCase()).join(", ")}</strong>
                      </p>
                    )}
                  </>
                )}

                {accountKind === "new" && !trialSession && (
                  <>
                    <h2 className="h6 fw-bold mb-3">Établissement / entreprise</h2>
                    <div className="row g-3 mb-4">
                      <div className="col-12">
                        <label className="form-label">Raison sociale *</label>
                        <input
                          className="form-control"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-12">
                        <GeoLocationFields
                          countryCode={countryCode}
                          stateCode={stateCode}
                          cityName={city}
                          onChange={(geo) => {
                            setCountryCode(geo.countryCode);
                            setCountryName(geo.countryName);
                            setStateCode(geo.stateCode);
                            setProvince(geo.stateName);
                            setCity(geo.cityName);
                            setPhoneDialCode((prev) =>
                              syncDialCodeFromCountry(geo.countryCode, prev),
                            );
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Numéro d&apos;impôt (NIF) *</label>
                        <input
                          className="form-control"
                          value={taxId}
                          onChange={(e) => setTaxId(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">RCCM</label>
                        <input
                          className="form-control"
                          value={rccm}
                          onChange={(e) => setRccm(e.target.value)}
                          placeholder="Optionnel"
                        />
                      </div>
                    </div>

                    <h2 className="h6 fw-bold mb-3">Administrateur</h2>
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <label className="form-label">Nom complet *</label>
                        <input
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <PhoneFieldWithDialCode
                          dialCode={phoneDialCode}
                          localNumber={phoneLocal}
                          onDialCodeChange={setPhoneDialCode}
                          onLocalNumberChange={setPhoneLocal}
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label">Email professionnel *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </>
                )}

                {signupMode === "pay" && paymentProvider === "mpesa" && accountKind === "new" && (
                  <div className="row g-3 mb-4">
                    <div className="col-12">
                      <label className="form-label">Numéro M-Pesa *</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={mpesaPhone}
                        onChange={(e) => setMpesaPhone(e.target.value)}
                        placeholder="+243 8XX XXX XXX"
                        required
                        minLength={9}
                      />
                      <div className="form-text">
                        Vodacom M-Pesa RDC — vous recevrez une demande de validation sur ce numéro.
                      </div>
                    </div>
                  </div>
                )}

                <p className="small text-secondary mb-3">
                  Formule :{" "}
                  <strong>
                    {signupMode === "pay" ? paymentLabel ?? "Paiement" : trialTitle}
                  </strong>
                  {" · "}
                  Facturation : <strong>{billingCycleLabel(billingCycle)}</strong>
                  {" · "}
                  Modules :{" "}
                  <strong>
                    {selected.length <= 4
                      ? selected.map((s) => s.toUpperCase()).join(", ")
                      : `${selected.length} modules`}
                  </strong>
                </p>

                {error && <div className="alert alert-danger">{error}</div>}
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(2)}
                    disabled={submitting}
                  >
                    ← Formule
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg flex-grow-1"
                    disabled={
                      submitting ||
                      (!accountKind && !trialSession) ||
                      ((accountKind === "existing" || trialSession) &&
                        modulesToActivate.length === 0)
                    }
                  >
                    {submitting
                      ? "Activation…"
                      : accountKind === "existing" || trialSession
                        ? signupMode === "pay"
                          ? "Continuer →"
                          : "Activer les modules →"
                        : signupMode === "pay"
                          ? "Continuer →"
                          : "Continuer →"}
                  </button>
                </div>
              </form>
            )}

            {step === 4 && accountKind === "new" && !trialSession && (
              <TrialTenantSlugStep
                company={company}
                city={city}
                value={tenantSlug}
                onChange={setTenantSlug}
                onBack={() => {
                  setError(null);
                  setStep(3);
                }}
                onConfirm={runActivation}
                submitting={submitting}
                error={error}
                confirmLabel={
                  signupMode === "pay"
                    ? `Payer avec ${paymentLabel ?? "…"} →`
                    : "Activer l'essai →"
                }
              />
            )}

            {step < 5 && (
            <div className="text-center mt-4">
              {industrySlug ? (
                <Link to={`/industries/${industrySlug}`}>← Retour au secteur</Link>
              ) : (
                <Link to="/products">← Retour au catalogue</Link>
              )}
            </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
